import { useEffect, useRef, useState } from "react";
import { FileText, Linkedin, MessagesSquare, Briefcase, Heart, Globe2, AlertTriangle } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import FinalCTA from "@/components/shared/FinalCTA";

// Industry research: what's missing in employee transitions today
const INDUSTRY_STATS = [
  {
    value: 45,
    suffix: "%",
    text: "of organisational restructurings prioritise process efficiency over employee experience.",
  },
  {
    value: 60,
    suffix: "%",
    text: "of employees in transition report limited personal guidance beyond formal procedures.",
  },
  {
    value: 50,
    suffix: "%",
    text: "of workforce transitions focus on compliance, not confidence or continuity.",
  },
  {
    value: 80,
    suffix: "%",
    text: "of transition programmes exclude structured human support.",
  },
];

// Our commitment & outcome metrics
const COMMITMENT_STATS = [
  {
    value: 100,
    suffix: "%",
    text: "commitment from our end to support you fully.",
  },
  {
    value: 80,
    suffix: "%",
    text: "of participants report feeling more hopeful after our sessions.",
  },
];

const SERVICES = [
  { icon: FileText, title: "CV & Cover Letter", text: "ATS-friendly, story-led documents that get noticed in the Dutch market." },
  { icon: Linkedin, title: "LinkedIn Optimisation", text: "Profile rebuilds that surface you to recruiters and hiring managers." },
  { icon: MessagesSquare, title: "Interview Coaching", text: "Practice rounds and feedback for Dutch and international interview styles." },
  { icon: Briefcase, title: "Job Search Strategy", text: "Targeted search planning across roles, sectors and hidden markets." },
  { icon: Heart, title: "Confidence & Resilience", text: "Rebuilding self-belief and momentum after redundancy or stalled progress." },
  { icon: Globe2, title: "Dutch Workplace Culture", text: "Navigate the unwritten rules of communication, hierarchy and feedback." },
];

function Counter({ value, suffix, color = "#1E2761", size = "text-5xl md:text-6xl" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          const duration = 1600;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setN(Math.floor(eased * value));
            if (t < 1) requestAnimationFrame(tick);
            else setN(value);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, started]);

  return (
    <span
      ref={ref}
      className={`font-display ${size} tracking-tight whitespace-nowrap`}
      style={{ color }}
    >
      {n.toLocaleString()}{suffix}
    </span>
  );
}

export default function Outplacement() {
  return (
    <div data-testid="outplacement-page">
      <PageHero
        eyebrow="Career Transition Support, Netherlands"
        headline="Your Career Transition, Handled with Care."
        subheadline="The Netherlands is home to one of Europe's largest international professional communities and navigating a career transition here, especially as an expat, comes with unique challenges. The language barriers, the unfamiliar market, the emotional weight of being in transition far from home. You don't have to do this alone."
        ctaLabel="Talk to Us. It's Free"
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216"
        imageAlt="A confident professional in transition, ready for the next role"
      />

      {/* INDUSTRY PROBLEM SECTION */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="industry-stats">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionEyebrow className="mb-6">The Gap We Fill</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight mb-8">
              Employee Transitions Are More Than Operational Tasks.
            </h2>
            <div className="space-y-5 text-base md:text-lg text-[#334155]/85 leading-relaxed">
              <p>
                Workforce changes coming from restructuring, layoffs or role
                shifts are often handled as processes. Timelines, procedures
                and documentation take priority.
              </p>
              <p>
                What is missing for employees is{" "}
                <span className="font-semibold text-[#1E2761]">human support</span>,
                someone to help them process the change, understand next steps,
                and maintain dignity and confidence.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
              {INDUSTRY_STATS.map((s, i) => (
                <div
                  key={i}
                  data-testid={`industry-stat-${i}`}
                  className="bg-white p-8 md:p-10 hover:bg-[#F4F7FC] transition-colors min-h-[220px] flex flex-col"
                >
                  <div className="flex items-baseline gap-3 mb-5">
                    <Counter value={s.value} suffix={s.suffix} size="text-5xl md:text-6xl" />
                  </div>
                  <div className="w-10 h-[2px] bg-[#C9A84C] mb-4" />
                  <p className="text-sm md:text-base text-[#334155]/85 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 text-xs text-[#334155]/60 leading-relaxed">
              <AlertTriangle size={14} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
              <span>
                Indicative industry research on workforce transition practices
                across European organisations.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* EMPATHY FIRST SECTION */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="empathy-first">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <SectionEyebrow className="mb-6">Our Promise</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight mb-6">
              We Believe in Empathy-First Support.
            </h2>
            <p className="text-base md:text-lg text-[#334155]/85 leading-relaxed">
              At NextChapter4You, we understand the emotional and practical
              challenges of losing a job. Our approach is rooted in compassion,
              confidentiality and genuine human connection, helping you
              navigate this difficult time with confidence and hope.
            </p>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
              {COMMITMENT_STATS.map((s, i) => (
                <div
                  key={i}
                  data-testid={`commitment-stat-${i}`}
                  className="bg-white p-8 md:p-10 min-h-[220px] flex flex-col"
                >
                  <Counter
                    value={s.value}
                    suffix={s.suffix}
                    color="#C9A84C"
                    size="text-5xl md:text-6xl"
                  />
                  <div className="w-10 h-[2px] bg-[#1E2761] mt-4 mb-4" />
                  <p className="text-sm md:text-base text-[#334155]/85 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE STATEMENT */}
      <section className="bg-white py-20 md:py-28 px-6 md:px-10" data-testid="emotional-support">
        <div className="max-w-4xl mx-auto bg-[#F4F7FC] border-l-4 border-[#C9A84C] p-10 md:p-14">
          <p className="font-display text-xl md:text-2xl text-[#1E2761] leading-snug italic">
            "Job transition is one of life's most stressful experiences. At
            NextChapter4You, we combine practical career support with genuine
            human coaching, because your confidence and wellbeing matter as
            much as your CV."
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="outplacement-services">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <SectionEyebrow className="mb-4">What's Included</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight">
              Everything You Need to Find Your Next Role.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`outplacement-service-${i}`}
                  className="bg-white p-8 md:p-10 hover:bg-[#F4F7FC] transition-colors group"
                >
                  <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-5 text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl text-[#1E2761] mb-3 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#334155]/80 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA
        headline="A Better Next Role Starts with One Conversation."
        subtext="Whether you need a polished CV, full outplacement support, or just someone to talk through your options with, we're here."
        ctaLabel="Book a Free Discovery Call"
      />
    </div>
  );
}
